import type { ApprovalStage, ThoughtTraceItem } from '@/types/workflow'

const JSON_FIELD_LABELS: Record<string, string> = {
  title: '标题',
  name: '名称',
  label: '标签',
  description: '说明',
  content: '内容',
  badge: '标记',
  summary: '摘要',
}

export function nextStageLabel(stage?: ApprovalStage) {
  if (stage === 'proposal') return '剧本生成'
  if (stage === 'script') return '分镜生成'
  if (stage === 'storyboard') return '成品制作'
  return '下一阶段'
}

export function getNextApprovalStage(stage: ApprovalStage): ApprovalStage {
  if (stage === 'proposal') return 'script'
  if (stage === 'script') return 'storyboard'
  return 'final'
}

export function traceStatusLabel(status: ThoughtTraceItem['status']) {
  const labels: Record<ThoughtTraceItem['status'], string> = {
    completed: '已完成',
    active: '进行中',
    pending: '待开始',
    failed: '失败',
  }
  return labels[status]
}

export function traceStatusType(status: ThoughtTraceItem['status']) {
  if (status === 'completed') return 'success'
  if (status === 'active') return 'warning'
  if (status === 'failed') return 'danger'
  return 'info'
}

export function getTraceDetailStage(item: ThoughtTraceItem) {
  const stageMap: Record<string, ApprovalStage> = {
    demand_analysis: 'proposal',
    proposal_generation: 'proposal',
    script_generation: 'script',
    storyboard_generation: 'storyboard',
    asset_generation: 'final',
    video_synthesis: 'final',
    compliance_check: 'final',
    final_packaging: 'final',
  }

  return stageMap[item.stage] || null
}

export function formatWorkspaceDate(value?: string | null) {
  if (!value) {
    return '刚刚'
  }
  return new Date(value).toLocaleString('zh-CN')
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isPrimitiveJsonValue(value: unknown) {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}

function formatJsonFieldLabel(key: string) {
  return JSON_FIELD_LABELS[key] || key
}

function formatJsonPrimitive(value: unknown) {
  if (typeof value === 'string') {
    return value.trim()
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return ''
}

function formatJsonObjectLines(value: Record<string, unknown>, index?: number, indent = ''): string[] {
  const lines: string[] = []
  const heading = [value.title, value.name, value.label]
    .map((item) => formatJsonPrimitive(item))
    .find(Boolean)

  if (index !== undefined) {
    lines.push(`${indent}${index + 1}. ${heading || '条目'}`)
  } else if (heading) {
    lines.push(`${indent}${heading}`)
  }

  Object.entries(value).forEach(([key, fieldValue]) => {
    if (fieldValue == null) {
      return
    }

    if (heading && ['title', 'name', 'label'].includes(key)) {
      return
    }

    if (Array.isArray(fieldValue)) {
      if (!fieldValue.length) {
        return
      }

      if (fieldValue.every(isPrimitiveJsonValue)) {
        const joinedValue = fieldValue.map((item) => formatJsonPrimitive(item)).filter(Boolean).join('、')
        if (joinedValue) {
          lines.push(`${indent}${formatJsonFieldLabel(key)}：${joinedValue}`)
        }
        return
      }

      const nestedLines = formatJsonValueLines(fieldValue, `${indent}  `)
      if (nestedLines.length) {
        lines.push(`${indent}${formatJsonFieldLabel(key)}：`)
        lines.push(...nestedLines)
      }
      return
    }

    if (isPlainRecord(fieldValue)) {
      const nestedLines = formatJsonObjectLines(fieldValue, undefined, `${indent}  `)
      if (nestedLines.length) {
        lines.push(`${indent}${formatJsonFieldLabel(key)}：`)
        lines.push(...nestedLines)
      }
      return
    }

    const primitiveValue = formatJsonPrimitive(fieldValue)
    if (primitiveValue) {
      lines.push(`${indent}${formatJsonFieldLabel(key)}：${primitiveValue}`)
    }
  })

  return lines
}

function formatJsonValueLines(value: unknown, indent = ''): string[] {
  if (Array.isArray(value)) {
    if (!value.length) {
      return []
    }

    if (value.every(isPrimitiveJsonValue)) {
      const joinedValue = value.map((item) => formatJsonPrimitive(item)).filter(Boolean).join('、')
      return joinedValue ? [`${indent}${joinedValue}`] : []
    }

    return value.flatMap((item, index) => {
      if (isPlainRecord(item)) {
        return [...(index > 0 ? [''] : []), ...formatJsonObjectLines(item, index, indent)]
      }

      const primitiveValue = formatJsonPrimitive(item)
      return primitiveValue ? [`${indent}${index + 1}. ${primitiveValue}`] : []
    })
  }

  if (isPlainRecord(value)) {
    return formatJsonObjectLines(value, undefined, indent)
  }

  const primitiveValue = formatJsonPrimitive(value)
  return primitiveValue ? [`${indent}${primitiveValue}`] : []
}

function buildJsonCandidate(lines: string[], startIndex: number) {
  let depth = 0
  const collected: string[] = []

  for (let index = startIndex; index < lines.length; index += 1) {
    const line = lines[index]
    collected.push(line)

    for (const char of line) {
      if (char === '[' || char === '{') {
        depth += 1
      } else if (char === ']' || char === '}') {
        depth -= 1
      }
    }

    if (depth === 0) {
      return {
        endIndex: index,
        text: collected.join('\n'),
      }
    }
  }

  return null
}

function formatEmbeddedJsonBlocks(value: string) {
  const lines = value.split('\n')
  const result: string[] = []

  for (let index = 0; index < lines.length; index += 1) {
    const trimmed = lines[index].trim()
    const mayBeJsonStart = trimmed === '['
      || trimmed === '{'
      || /^[\[{]\s*[{\["0-9tfn-]/.test(trimmed)

    if (!mayBeJsonStart) {
      result.push(lines[index])
      continue
    }

    const candidate = buildJsonCandidate(lines, index)
    if (!candidate) {
      result.push(lines[index])
      continue
    }

    try {
      const parsed = JSON.parse(candidate.text)
      const formattedLines = formatJsonValueLines(parsed)
      if (formattedLines.length) {
        result.push(...formattedLines)
        index = candidate.endIndex
        continue
      }
    } catch {
      // Keep original lines when the block is not valid JSON.
    }

    result.push(lines[index])
  }

  return result.join('\n')
}

function normalizeJsonLikeLine(line: string) {
  const trimmed = line.trim()

  if (/^[\[{]\s*$/.test(trimmed) || /^[\]}]\s*,?\s*$/.test(trimmed)) {
    return null
  }

  const jsonFieldMatch = trimmed.match(/^"([^"]+)"\s*:\s*(.+?)\s*,?$/)
  if (!jsonFieldMatch) {
    return line
  }

  const [, rawKey, rawValue] = jsonFieldMatch
  const key = formatJsonFieldLabel(rawKey)
  const normalizedValue = rawValue
    .trim()
    .replace(/^"/, '')
    .replace(/"$/, '')
    .replace(/\\$/, '')
    .trim()

  return normalizedValue ? `${key}：${normalizedValue}` : null
}

export function normalizeEditorContent(value: string): string {
  return formatEmbeddedJsonBlocks(value)
    .replace(/\r\n?/g, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .split('\n')
    .map((line) => line.trimEnd())
    .map((line) => normalizeJsonLikeLine(line))
    .filter((line): line is string => line !== null)
    .filter((line) => !/^\|\s*:?-{3,}.*\|\s*$/.test(line))
    .map((line) => {
      if (!/^\|.*\|\s*$/.test(line)) {
        return line
      }

      const columns = line
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean)

      return columns.join(' / ')
    })
    .join('\n')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[*-]\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function buildStoryboardEditorText(storyboard: Record<string, any>): string {
  if (storyboard.edited_storyboard_text) {
    return normalizeEditorContent(String(storyboard.edited_storyboard_text))
  }

  const scenes = Array.isArray(storyboard.scenes) ? storyboard.scenes : []
  return normalizeEditorContent(scenes
    .map((scene: Record<string, any>, index: number) => {
      const title = scene.location || scene.description || scene.action || `场景 ${index + 1}`
      const description = scene.description ? `画面描述：${String(scene.description).trim()}` : ''
      const characters = Array.isArray(scene.characters) && scene.characters.length
        ? `角色：${scene.characters.map((item: unknown) => String(item || '').trim()).filter(Boolean).join('、')}`
        : ''
      const dialogue = scene.dialogue_summary ? `对白重点：${String(scene.dialogue_summary).trim()}` : ''
      const visualCues = Array.isArray(scene.visual_cues) && scene.visual_cues.length
        ? `视觉线索：${scene.visual_cues.map((item: unknown) => String(item || '').trim()).filter(Boolean).join('、')}`
        : ''
      const mood = scene.mood ? `情绪氛围：${String(scene.mood).trim()}` : ''
      const backgroundPrompt = scene.background_prompt ? `背景提示：${String(scene.background_prompt).trim()}` : ''
      const duration = scene.duration || scene.estimated_duration ? `建议时长：${scene.duration || scene.estimated_duration} 秒` : ''
      return [
        `场景 ${index + 1}：${title}`,
        description,
        characters,
        dialogue,
        visualCues,
        mood,
        backgroundPrompt,
        duration,
      ].filter(Boolean).join('\n')
    })
    .join('\n\n'))
}